<template>
  <div class="max-w-lg w-full mx-auto">

    <!-- Error detected with file upload -->
    <div v-if="isFileUploadModalVisible" class="fixed inset-0 bg-red-500 bg-opacity-75 flex items-center justify-center z-50">
      <div class="bg-white p-8 rounded-lg shadow-lg w-96">
        <div class="flex items-center">
          <span class="text-3xl pr-4">⚠️</span>
          <h2 class="text-xl font-bold text-red-600">Warning: We've detected an issue</h2>
        </div>
        <p class="mt-4 text-lg">{{ fileUploadMessage }}</p>
        <div class="mt-6 flex justify-between">
          <button @click="acceptError(setSourceFileToNull)" class="bg-gray-500 text-white py-2 px-4 rounded hover:bg-gray-600">
            Accept error
          </button>
        </div>
      </div>
    </div>

    <!-- Show All ceremonies warning modal -->
    <div v-if="showAll && isWarningModalVisible" class="fixed inset-0 bg-red-500 bg-opacity-75 flex items-center justify-center z-50">
      <div class="bg-white p-8 rounded-lg shadow-lg w-96">
        <div class="flex items-center">
          <span class="text-3xl pr-4">⚠️</span>
          <h2 class="text-xl font-bold text-red-600">Warning: Potentially Dangerous Action</h2>
        </div>
        <p class="mt-4 text-lg">You are about view ceremonies that have already been uploaded. Please carefully select the ceremony you wish to upload and proceed with <b>caution</b> or cancel.</p>
        <div class="mt-6 flex justify-between">
          <button @click="showAllCeremonies(false)" class="bg-gray-500 text-white py-2 px-4 rounded hover:bg-gray-600">
            Cancel
          </button>
          <button @click="closeWarningModal" class="bg-red-600 text-white py-2 px-4 rounded hover:bg-red-700">
            Proceed
          </button>
        </div>
      </div>
    </div>

    <!-- Show All ceremony overwrite modal -->
    <div v-if="isCeremonyOverwriteModalVisible" class="fixed inset-0 bg-red-500 bg-opacity-75 flex items-center justify-center z-50">
      <div class="bg-white p-8 rounded-lg shadow-lg md:w-2/4 w-96 ">
        <div class="flex items-center">
          <span class="text-3xl pr-4">⚠️</span>
          <h2 class="md:text-2xl text-xl font-bold text-red-600">Warning: Potentially Dangerous Action</h2>
        </div>
        <p class="mt-4 text-lg">You are about to upload footage of a ceremony that may already have existing footage. To continue, 
          please verify that both the ceremony and the file are correct, then enter 
          <span style="user-select: none;">'Permanently Overwrite'</span> and click Overwrite. If you wish to cancel, select Cancel.
        </p>
        <p class="mt-4 text-md"> Ceremony:
          <b>
            {{ formatCeremonyOption(ceremonies.find(x => x.id == selected_ceremony) || null) }}
          </b>
        </p>
        <p class="my-2 text-md">File: <b>{{ source_file.name }}</b></p>
        <div class="flex flex-col">
          <input 
            type="text" 
            class="p-2 my-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" 
            placeholder="Permanently Overwrite"
            v-model="validateString"
          >
        </div>
        <div class="mt-6 flex justify-between">
          <button @click="isCeremonyOverwriteModalVisible = false" class="bg-gray-500 text-white py-2 px-4 rounded hover:bg-gray-600">
            Cancel
          </button>
          <button 
            id="overwriteModalSubmitBtn"
            @click="successOverwriteValidation" 
            :disabled="!isStringOverwriteValid"
            class="bg-red-600 text-white py-2 px-4 rounded hover:bg-red-700 disabled:opacity-75"
          >
            Overwrite
          </button>
        </div>
      </div>
    </div>

    <div v-if="completed" class="flex flex-wrap -mx-2 space-y-4 md:space-y-0">
      <div class="mt-16 mx-auto max-w-7xl px-4 sm:mt-24 sm:px-6">
        <div class="text-center">
          <h1
            class="text-4xl tracking-tight font-extrabold text-gray-900 sm:text-5xl md:text-6xl"
          >
            <span class="block">Upload Completed</span>
          </h1>
          <p
            class="mt-3 max-w-md mx-auto text-base text-gray-700 sm:text-lg md:mt-5 md:text-xl md:max-w-3xl"
          >
            You uploaded a source file for {{ selected_university["name"] }}
            {{ formatCeremonyOption(ceremonies.find(x => x.id == selected_ceremony) || null) }}
          </p>
        </div>
      </div>
    </div>

    <form class="space-y-4 text-gray-700" @submit.prevent v-if="!completed">
      <div class="flex flex-wrap -mx-2 space-y-4 md:space-y-0">
        <div class="mt-8 mx-auto max-w-7xl px-4 sm:mt-4 sm:px-6">
          <div class="text-center">
            <h1
              class="text-2xl tracking-tight font-extrabold text-gray-900 sm:text-2xl md:text-2xl"
            >
              <span class="block text-mygrad">Upload Source File</span>
            </h1>
          </div>
        </div>
      </div>
      <div class="flex flex-wrap -mx-2 space-y-4 md:space-y-0">
        <div class="w-full px-2">
          <button
            v-if="!showAll"
            type="submit"
            @click="showAllCeremonies(true)"
            class="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Show All Ceremonies (warning this will include ceremonies you have
            already uploaded)
          </button>
          <button
            v-else
            type="submit"
            @click="showAllCeremonies(false)"
            class="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Hide already uploaded ceremonies (Normal mode)
          </button>
        </div>
      </div>
      <div class="flex flex-wrap -mx-2 space-y-4 md:space-y-0">
        <div class="w-full px-2">
          <h2 v-if="stage >= 0 && universities.length > 0">
            Select University
          </h2>
          <select
            class="w-full h-10 px-3 text-base placeholder-gray-600 border rounded-lg focus:shadow-outline"
            v-model="selected_university"
            @change="onUniChange"
            v-if="stage >= 0 && universities.length > 0"
          >
            <option disabled :value="null">Select University</option>
            <option
              v-for="(uni, c_index) in this.universities"
              :key="c_index"
              :value="uni"
            >
              {{ uni["name"] }}
            </option>
          </select>
        </div>
      </div>
      <div class="flex flex-wrap -mx-2 space-y-4 md:space-y-0">
        <div class="w-full px-2">
          <h2 v-if="stage >= 1">Now Select Ceremony</h2>
          <select
            v-if="stage >= 1"
            v-model="selected_ceremony"
            class="w-full h-10 px-3 text-base placeholder-gray-600 border rounded-lg focus:shadow-outline"
          >
            <option disabled :value="null">Select your event date</option>

            <option
              v-for="ceremony in ceremonies"
              :value="ceremony['id']"
              :key="ceremony['id']"
            >
              {{ formatCeremonyOption(ceremony) }}
            </option>
          </select>
        </div>
      </div>
      <div class="flex flex-wrap -mx-2 space-y-4 md:space-y-0">
        <div class="w-full px-2">
          <h2 v-if="stage >= 1">Use this source for</h2>
          <div v-if="stage >= 1" class="mt-2 grid grid-cols-1 gap-2 text-sm">
            <label class="flex items-center gap-2 rounded border border-gray-300 p-2">
              <input type="checkbox" value="clips" v-model="source_usages" checked/>
              <span>Personalised clips</span>
            </label>
            <label class="flex items-center gap-2 rounded border border-gray-300 p-2">
              <input type="checkbox" value="digital_download" v-model="source_usages" />
              <span>Full ceremony digital download</span>
            </label>
          </div>
        </div>
      </div>
      <div class="flex flex-wrap -mx-2 space-y-4 md:space-y-0">
        <div class="w-full px-2">
          <input
            class="w-full h-10 px-3 text-base placeholder-gray-600 border rounded-lg focus:shadow-outline"
            type="file"
            name="file"
            ref="file"
            accept="video/mp4"
            @change="onFileChange"
            v-if="selected_ceremony != null"
          />
        </div>
      </div>

      <div class="flex flex-wrap -mx-2 space-y-4 md:space-y-0">
        <div class="w-full px-2">
          <video-player
            v-if="source_file"
            :options="videoOptions"
            ref="videoPlayer"
          ></video-player>
        </div>
      </div>

      <div
        class="flex flex-wrap -mx-2 space-y-4 md:space-y-0"
        v-if="clipProcessingRequested && source_file != null"
      >
        <div class="w-full px-2">
          <h3>Clip window</h3>
          <div class="mt-2 flex flex-wrap gap-2 text-sm">
            <button
              type="button"
              @click="markClipWindow('start')"
              class="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              Mark clip start
            </button>
            <button
              type="button"
              @click="markClipWindow('end')"
              class="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              Mark clip end
            </button>
            <span v-if="clipWindow.startSeconds !== null" class="px-2 py-2 rounded bg-gray-100">
              Start: {{ formatSeconds(clipWindow.startSeconds) }}
            </span>
            <span v-if="clipWindow.endSeconds !== null" class="px-2 py-2 rounded bg-gray-100">
              End: {{ formatSeconds(clipWindow.endSeconds) }}
            </span>
          </div>
          <p
            v-if="clipWindowError"
            class="mt-2 text-sm text-red-600"
          >
            {{ clipWindowError }}
          </p>
          <p
            v-if="sourceUploadLimitExceeded"
            class="mt-2 text-sm text-red-600"
          >
            This file is over the current 6GB source upload limit. Please upload a smaller source file.
          </p>
        </div>
      </div>

      <div
        class="flex flex-wrap -mx-2 space-y-4 md:space-y-0"
        v-if="clipProcessingRequested && source_file != null"
      >
        <div class="w-full px-2">
          <h3>
            Now please go to a reference frame for the first walk shot, press
            pause and then press Create First Reference
          </h3>
          <button
            type="submit"
            @click="createReference('wide')"
            class="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Create First Reference
          </button>
        </div>
      </div>

      <div
        class="flex flex-wrap -mx-2 space-y-4 md:space-y-0"
        v-if="clipProcessingRequested && references.wide != null"
      >
        <div class="w-full px-2">
          <h3>
            Now please go to a reference frame for the SECOND walk shot, press
            pause and then press Create SECOND Reference
          </h3>
          <button
            type="submit"
            @click="createReference('close')"
            class="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Create Second Reference
          </button>
        </div>
      </div>
      <div class="flex flex-wrap -mx-2 space-y-4 md:space-y-0">
        <div class="w-full px-2">
          <button
            type="submit"
            @click="checkForOverwrite"
            class="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            v-if="canSubmitUpload"
            :disabled="isUploading"
          >
            Submit
          </button>
          <div
            class="w-full bg-gray-200 rounded-full dark:bg-gray-700"
            v-if="stage > 50 || stage == -1"
          >
            <div
              class="bg-blue-600 text-xs font-medium text-blue-100 text-center p-0.5 leading-none rounded-full"
              :style="'width: ' + progress + '%'"
            >
              {{ progress }}%
            </div>
          </div>
          <p v-if="uploadStatus" class="mt-2 text-sm text-gray-700">
            {{ uploadStatus }}
          </p>
        </div>
      </div>
    </form>
  </div>
</template>


<script>
import axios from "axios";
import "videojs-contrib-hls";
import { trackAdminEvent } from '../../admin/activity.js'

const MAX_PARALLEL_PART_UPLOADS = 3;
const MAX_PART_UPLOAD_ATTEMPTS = 3;
const PART_UPLOAD_RETRY_BASE_DELAY_MS = 1000;

export default {
  name: "Upload",
  data: function () {
    return {
      stage: 0,
      isUploading: false,
      progress: false,
      universities: [],
      selected_university: null,
      ceremonies: [],
      selected_ceremony: null,
      source_file: null,
      source_usages: ["clips"],
      references: {
        wide: null,
        close: null,
      },
      clipWindow: {
        startSeconds: null,
        endSeconds: null,
      },
      completed: false,
      showAll: false,
      isWarningModalVisible: true,
      isCeremonyOverwriteModalVisible: false,
      validateString: '',
      fileUploadMessage: '',
      isFileUploadModalVisible: false,
      setSourceFileToNull: false,
      clipWindowError: '',
      uploadStatus: '',
    };
  },
  computed: {
    videoOptions() {
      var URL = window.URL || window.webkitURL;
      var fileURL = URL.createObjectURL(this.source_file);

      return {
        sources: [
          {
            type: "video/mp4",
            src: fileURL,
          },
        ],
        aspectRation: "16:9",
        responsive: true,
      };
    },
    player() {
      return this.$refs.videoPlayer.player;
    },
    isStringOverwriteValid() {
      return this.validateString === 'Permanently Overwrite'
    },
    clipProcessingRequested() {
      return this.source_usages.includes("clips")
    },
    canSubmitUpload() {
      if (!this.source_usages.length) return false
      if (!this.source_file) return false
      if (this.sourceUploadLimitExceeded) return false
      if (this.clipWindowError) return false
      if (
        (this.clipWindow.startSeconds !== null || this.clipWindow.endSeconds !== null) &&
        this.clipWindowPayload === null
      ) return false
      if (!this.clipProcessingRequested) return true
      return this.references.wide != null && this.references.close != null
    },
    sourceUploadLimitExceeded() {
      return this.source_file && this.source_file.size > 6 * 1024 * 1024 * 1024
    },
    clipWindowPayload() {
      if (this.clipWindow.startSeconds === null && this.clipWindow.endSeconds === null) return null
      if (this.clipWindow.startSeconds === null || this.clipWindow.endSeconds === null) return null
      if (this.clipWindow.endSeconds <= this.clipWindow.startSeconds) return null
      return {
        startSeconds: Math.floor(this.clipWindow.startSeconds),
        endSeconds: Math.floor(this.clipWindow.endSeconds),
      }
    }
  },
  beforeCreate() {
    axios
      .get(`/universities`)
      .then((response) => response.data)
      .then((response_data) => (this.universities = response_data))
      .catch(() => this.$router.push("/admin/login"));
  },
  mounted() {
    var showAll = this.$route.query.showAll;
    this.showAll = showAll == "true";
  },
  methods: {
    validateCeremonyOverwrite() {
      this.validateString = ''
      this.isCeremonyOverwriteModalVisible = true
    },
    closeWarningModal() {
      this.isWarningModalVisible = false;
    },
    showAllCeremonies(e) {
      this.$router.push({ path: "/admin/upload", query: { showAll: e } });
      this.$router.go();
    },
    createReference(t) {
      // Force user to scroll to a point in the video
      if (this.player.currentTime() === 0) {
        this.fileUploadMessage = "Please set an appropriate reference point.";
        this.setSourceFileToNull = false;
        this.isFileUploadModalVisible = true;
        return;
      }

      if (t == "wide") {
        // TODO: tidy reference
        this.references.wide = this.player.currentTime();
      } else {
        this.references.close = this.player.currentTime();
      }
    },
    markClipWindow(edge) {
      if (!this.player || this.player.currentTime() === undefined) return
      const seconds = Math.floor(this.player.currentTime())
      if (edge === 'start') {
        this.clipWindow.startSeconds = seconds
      } else {
        this.clipWindow.endSeconds = seconds
      }
      if (
        this.clipWindow.startSeconds !== null &&
        this.clipWindow.endSeconds !== null &&
        this.clipWindow.endSeconds <= this.clipWindow.startSeconds
      ) {
        this.clipWindowError = 'Clip end must be after clip start.'
      } else {
        this.clipWindowError = ''
      }
    },
    formatSeconds(value) {
      const seconds = Number(value || 0)
      const minutes = Math.floor(seconds / 60)
      const remaining = seconds % 60
      return `${minutes}:${String(remaining).padStart(2, '0')}`
    },

    async getSignedUrl(ceremony_id, file_name, references, type = "FILE") {
      if (this.clipWindowError) {
        throw new Error(this.clipWindowError)
      }
      if (
        (this.clipWindow.startSeconds !== null || this.clipWindow.endSeconds !== null) &&
        this.clipWindowPayload === null
      ) {
        throw new Error('Clip window must include a start and end time.')
      }
      return axios
        .post(`/ceremonies/${ceremony_id}/upload`, {
          filename: file_name,
          references: references,
          type: type,
          usages: this.source_usages,
          fileSize: this.source_file ? this.source_file.size : undefined,
          multipart: this.source_file ? this.source_file.size >= 4 * 1024 * 1024 * 1024 : false,
          clipWindow: this.clipWindowPayload,
          processingProfile: this.source_usages.length === 1 && this.source_usages[0] === "digital_download"
            ? "download_only"
            : this.source_usages.includes("digital_download")
              ? "clip_and_download"
              : "clip_source"
        })
        .then((response) => response.data);
    },
    async checkForOverwrite() {
      // Display modal if showAll is true
      const isOverwritePotential = this.showAll
      if(isOverwritePotential){
        return this.validateCeremonyOverwrite()
      }
      // showAll is false, submit
      return this.submitForUpload()
    },
    async successOverwriteValidation(){
      // Hide modal and submit
      this.isCeremonyOverwriteModalVisible = false 
      this.submitForUpload()
    },
    async submitForUpload() {
      this.submitSourceFile();
    },
    async submitSourceFile() {
      this.stage = 100;
      this.isUploading = true;
      this.uploadStatus = 'Preparing upload...'
      try {
        var signedUrl = await this.getSignedUrl(
          this.selected_ceremony,
          this.source_file.name,
          this.references
        );
        signedUrl = signedUrl.url
        this.progress = 0;

        if (signedUrl.uploadMethod === "multipart") {
          await this.uploadMultipartSourceFile(signedUrl)
          this.completed = true
          this.uploadStatus = ''
          this.trackSourceUploadCompleted()
          return
        }

        var formData = new FormData();
        for (const [key, value] of Object.entries(signedUrl.fields)) {
          formData.append(key, value);
        }
        var file = new File([this.source_file], this.source_file.name);
        formData.append("file", file);
        await axios.post(signedUrl.url, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          transformRequest: (data, headers) => {
            delete headers.Authorization;
            delete headers.authorization;
            if (headers.common) {
              headers.common = { ...headers.common };
              delete headers.common["Authorization"];
              delete headers.common.authorization;
            }
            return data;
          },
          onUploadProgress: (progressEvent) => {
            this.stage = -1;
            let percentCompleted = Math.floor(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            // do whatever you like with the percentage complete
            // maybe dispatch an action that will update a progress bar or something
            this.progress = percentCompleted;
          },
        })
        this.completed = true;
        this.uploadStatus = ''
        this.trackSourceUploadCompleted()
      } catch (error) {
        console.log(`Error uploading file: ${error.message}`);
        this.fileUploadMessage = error.message || 'The upload failed. Please try again.'
        this.setSourceFileToNull = false
        this.isFileUploadModalVisible = true
        this.isUploading = false
        this.uploadStatus = ''
      }
    },
    trackSourceUploadCompleted() {
      trackAdminEvent('admin_action_completed', {
        toolKey: 'clip_upload',
        metadata: {
          action: 'source_upload_complete',
          ceremonyId: this.selected_ceremony,
          universityId: this.selected_university && this.selected_university.id,
          usages: this.source_usages,
          fileSize: this.source_file && this.source_file.size,
        },
      })
    },
    sleep(ms) {
      return new Promise((resolve) => setTimeout(resolve, ms));
    },
    partRetryDelay(attempt) {
      const baseDelay = PART_UPLOAD_RETRY_BASE_DELAY_MS * Math.pow(2, attempt - 1);
      const jitter = Math.floor(Math.random() * 250);
      return baseDelay + jitter;
    },
    updateMultipartProgress(partProgress) {
      const loaded = Object.values(partProgress).reduce((total, value) => total + value, 0);
      this.progress = Math.min(99, Math.floor((loaded * 100) / this.source_file.size));
    },
    normalizeETag(etag) {
      if (!etag) return etag;
      return Array.isArray(etag) ? etag[0] : etag;
    },
    async uploadMultipartPart(part, signedUrl, partProgress) {
      const file = this.source_file;
      const start = (part.partNumber - 1) * signedUrl.partSize;
      const end = Math.min(start + signedUrl.partSize, file.size);
      const blob = file.slice(start, end);
      let lastError = null;

      for (let attempt = 1; attempt <= MAX_PART_UPLOAD_ATTEMPTS; attempt += 1) {
        try {
          this.uploadStatus = `Uploading part ${part.partNumber} of ${signedUrl.parts.length}`
          if (attempt > 1) {
            this.uploadStatus = `Retrying part ${part.partNumber} of ${signedUrl.parts.length} (${attempt} of ${MAX_PART_UPLOAD_ATTEMPTS})`
          }
          partProgress[part.partNumber] = 0;
          this.updateMultipartProgress(partProgress);
          const result = await axios.put(part.url, blob, {
            headers: {
              "Content-Type": "video/mp4",
            },
            transformRequest: (data, headers) => {
              delete headers.common["Authorization"];
              return data;
            },
            onUploadProgress: (progressEvent) => {
              this.stage = -1;
              partProgress[part.partNumber] = progressEvent.loaded;
              this.updateMultipartProgress(partProgress);
            },
          });
          partProgress[part.partNumber] = blob.size;
          this.updateMultipartProgress(partProgress);
          return {
            ETag: this.normalizeETag(result.headers.etag),
            PartNumber: part.partNumber,
          };
        } catch (error) {
          lastError = error;
          partProgress[part.partNumber] = 0;
          this.updateMultipartProgress(partProgress);
          if (attempt < MAX_PART_UPLOAD_ATTEMPTS) {
            await this.sleep(this.partRetryDelay(attempt));
          }
        }
      }

      throw new Error(
        `Part ${part.partNumber} failed after ${MAX_PART_UPLOAD_ATTEMPTS} attempts: ${lastError && lastError.message ? lastError.message : 'upload failed'}`
      );
    },
    async uploadMultipartSourceFile(signedUrl) {
      const parts = [];
      const partProgress = {};
      let nextPartIndex = 0;

      const uploadNextPart = async () => {
        while (nextPartIndex < signedUrl.parts.length) {
          const part = signedUrl.parts[nextPartIndex];
          nextPartIndex += 1;
          const uploadedPart = await this.uploadMultipartPart(part, signedUrl, partProgress);
          parts.push(uploadedPart);
        }
      };

      const workerCount = Math.min(MAX_PARALLEL_PART_UPLOADS, signedUrl.parts.length);
      await Promise.all(Array.from({ length: workerCount }, uploadNextPart));

      const completedParts = parts.sort((a, b) => a.PartNumber - b.PartNumber);
      if (completedParts.length !== signedUrl.parts.length) {
        throw new Error('Not all upload parts completed. Please try again.');
      }

      this.uploadStatus = 'Completing upload...'
      await axios.post(`/ceremonies/${this.selected_ceremony}/upload`, {
        action: "complete_multipart_upload",
        key: signedUrl.key,
        sourceUploadId: signedUrl.uploadId ? (signedUrl.sourceUploadId || null) : null,
        uploadId: signedUrl.uploadId,
        parts: completedParts,
      });
      this.progress = 100;
      this.uploadStatus = ''
    },

    onUniChange() {
      this.getCeremonies(this.selected_university["id"]).then(
        (ceremonies) => (this.ceremonies = ceremonies)
      );
      this.stage = 1;
      this.selected_ceremony = null;
    },
    async getCeremonies(uni_id) {
      if (typeof this.showAll != "undefined" && this.showAll) {
        return axios
          .get(`/universities/${uni_id}/ceremonies`)
          .then((response) => response.data);
      } else {
        return axios
          .get(`/universities/${uni_id}/ceremonies?ready=false`)
          .then((response) => response.data);
      }
    },
    onFileChange(e) {
      var files = e.target.files || e.dataTransfer.files;
      if (!files.length) return;
      this.source_file = files[0];
      this.clipWindow = {
        startSeconds: null,
        endSeconds: null,
      }
      this.clipWindowError = ''
      this.validateFileUpload(this.source_file)
    },
    async validateFileUpload(file) {
      try {
        this.validateFileSize(file);
        // File passed validation checks
        return;
      } catch (error) {
        console.error(`Error validating file: ${error.message}`);
        this.fileUploadMessage = error.message;
        this.isFileUploadModalVisible = true;
        return;
      }
    },
    validateFileSize(file){
      const maxSizeInBytes = 6 * 1024 * 1024 * 1024;
      console.log(`File size: ${(file.size / (1000 * 1000 * 1000)).toFixed(2)} GB`);
      if (file.size > maxSizeInBytes) {
        this.setSourceFileToNull = true;
        throw new Error('This file is over the current 6GB source upload limit. Please upload a smaller source file.')
      }
      return true;
    },
    setSourceFile(v){
      this.source_file = v;
      this.$refs.file.value = v;
    },
    acceptError(sourceFileToNull) {
      this.isFileUploadModalVisible = false;
      if (sourceFileToNull == true)
        this.setSourceFile(null);
    },
    formatCeremonyOption(ceremony) {
      if (!ceremony) return "";
      const formattedDate = this.formatDate(ceremony.datetime);
      return ceremony.label
        ? `${formattedDate} - ${ceremony.label}`
        : formattedDate;
    },
    formatDate(dateString) {
      if (!dateString) return "";
      return new Date(dateString).toLocaleDateString("en-GB", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    },
  },

  components: {},
};
</script>
<style scoped>
video {
  min-width: 100%;
  min-height: 40em;
  position: relative !important;
}
.video-js {
  width: 100% !important;
  min-height: 40em !important;
  position: relative !important;
}
button#overwriteModalSubmitBtn:disabled{
  visibility: visible!important;
  background-color: grey;
  color: white;
  opacity: 0.5;
  cursor: none;
}
</style>
